import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import MyContext from '@/shared/contexts/AppContext';
import RelatoriosAprovacaoFilter from '@/modules/reports/presentation/components/RelatoriosAprovacaoFilter';
import RelatoriosAprovacaoList from '@/modules/reports/presentation/components/RelatoriosAprovacaoList';
import { AprovarRelatorio, BuscarRelatoriosParaAprovacao } from '@/modules/reports/application/relatorios-aprovacao.service';
import { Container, Alert, Modal, Button } from 'react-bootstrap';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { GetDiretoriaDoUsuario, GetDiretorias } from '@/modules/structure/diretoria/diretoria.service';
import { ReadDiretoriaDto } from '@/api.types';
import { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { usuarioTemSuporteAdministrativoRelatorios } from '@/modules/reports/application/relatorios-acesso.utils';
import RelatoriosUserLabel from '@/modules/reports/presentation/components/RelatoriosUserLabel';
export default function RelatoriosAprovacaoPage() {
  const context = useContext(MyContext);
  const navigate = useNavigate();

  if (!context) {
    throw new Error('RelatoriosAprovacaoPage deve ser usado dentro de um MyProvider');
  }

  const { authVisao } = context;

  // Estados
  const [loading, setLoading] = useState(true);
  const [relatorios, setRelatorios] = useState<RelatorioAtividadeDto[]>([]);
  const [diretoriaId, setDiretoriaId] = useState<number | null>(null);
  const [diretorias, setDiretorias] = useState<ReadDiretoriaDto[]>([]);
  const [aproving, setAproving] = useState(false);
  const [showApproveBatchModal, setShowApproveBatchModal] = useState(false);
  const [relatoriosParaAprovarLote, setRelatoriosParaAprovarLote] = useState<RelatorioAtividadeDto[]>([]);

  const isAdminSuporte = useMemo(
    () => usuarioTemSuporteAdministrativoRelatorios(authVisao),
    [authVisao],
  );

  // Carregar diretoria do usuário (diretor regional) ou todas (suporte administrativo)
  useEffect(() => {
    const carregarDiretoriaUsuario = async () => {
      try {
        const diretoriaUsuario = await GetDiretoriaDoUsuario();

        if (diretoriaUsuario) {
          setDiretoriaId(diretoriaUsuario.id);
          setDiretorias([diretoriaUsuario]);
        } else if (isAdminSuporte) {
          const resultado = await GetDiretorias(0, 500);
          const lista = resultado?.objects ?? [];
          setDiretorias(lista);
          if (lista.length === 1) {
            setDiretoriaId(lista[0].id);
          }
        } else {
          // Defensor sem diretoria: não tem permissão para aprovação
          setDiretorias([]);
        }
      } catch (error) {
        console.error('Erro ao carregar diretoria do usuário:', error);
        toast.error('Erro ao carregar diretoria');
        setDiretorias([]);
      } finally {
        setLoading(false);
      }
    };

    carregarDiretoriaUsuario();
  }, [isAdminSuporte]);

  // Redirecionar para lista de relatórios se não tiver diretoria (acesso direto pela URL), exceto suporte admin
  useEffect(() => {
    if (!loading && diretoriaId === null && diretorias.length === 0 && !isAdminSuporte) {
      navigate(Links.Relatorios, { replace: true });
    }
  }, [loading, diretoriaId, diretorias.length, navigate, isAdminSuporte]);

  // Carregar relatórios quando diretoria for selecionada
  useEffect(() => {
    if (diretoriaId) {
      carregarRelatorios();
    }
  }, [diretoriaId]);

  const carregarRelatorios = async (filters?: any) => {
    if (!diretoriaId) return;

    try {
      setLoading(true);
      const { statusNaoEnviados, statusEnviados, ...apiFilters } = filters || {};

      // Sem filtro de status = todos os status (enviado, aprovado, reprovado) para o diretor ver tramitação
      const relatoriosData = await BuscarRelatoriosParaAprovacao(diretoriaId, apiFilters);

      /**
       * Partição alinhada à UI (RelatoriosAprovacaoList):
       * - Ainda na fila do diretor / não encaminhados à Corregedoria: aguardando análise (enviado) ou reprovado pela diretoria (!dataAprovacao no reprovado).
       * - Já tramitados após o diretor (encaminhados à Corregedoria ou devolvidos por ela): aprovado pelo diretor, ou reprovado pela Corregedoria (reprovado + dataAprovacao).
       */
      const naFilaDoDiretor = (r: RelatorioAtividadeDto) =>
        r.status === 'enviado' || (r.status === 'reprovado' && !r.dataAprovacao);
      const posDiretorOuCorregedoria = (r: RelatorioAtividadeDto) =>
        r.status === 'aprovado' || (r.status === 'reprovado' && !!r.dataAprovacao);

      let processados = relatoriosData;
      if (statusNaoEnviados) {
        processados = relatoriosData.filter(naFilaDoDiretor);
      } else if (statusEnviados) {
        processados = relatoriosData.filter(posDiretorOuCorregedoria);
      }

      setRelatorios(processados);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (filters: any) => {
    await carregarRelatorios(filters);
  };

  const handleView = (relatorio: RelatorioAtividadeDto) => {
    // Navegar para visualização do relatório
    const params = new URLSearchParams({
      id: String(relatorio.id),
      defensoriaId: String(relatorio.defensoriaId),
      defensoriaName: relatorio.defensoriaName || '',
      mes: String(relatorio.mes).padStart(2, '0'),
      ano: String(relatorio.ano),
      tipo: relatorio.tipo,
      view: 'true', // Flag para modo visualização
      origem: 'diretoria',
    });
    navigate(`${Links.Relatorios_Criar}?${params.toString()}`);
  };

  const handleApproveBatch = (selecionados: RelatorioAtividadeDto[]) => {
    if (!diretoriaId || selecionados.length === 0) return;
    setRelatoriosParaAprovarLote(selecionados);
    setShowApproveBatchModal(true);
  };

  const confirmarAprovacaoLote = useCallback(async () => {
    if (!diretoriaId || relatoriosParaAprovarLote.length === 0) return;
    try {
      setAproving(true);
      let sucesso = 0;
      let falhas = 0;
      for (const r of relatoriosParaAprovarLote) {
        try {
          await AprovarRelatorio(r.id, diretoriaId);
          sucesso++;
        } catch {
          falhas++;
        }
      }
      if (sucesso > 0) {
        toast.success(`${sucesso} relatório(s) aprovado(s) com sucesso.`);
        await carregarRelatorios();
      }
      if (falhas > 0) {
        toast.error(`${falhas} relatório(s) não puderam ser aprovados.`);
      }
      setShowApproveBatchModal(false);
      setRelatoriosParaAprovarLote([]);
    } catch (error: any) {
      const mensagem = error?.response?.data?.message || error?.message || 'Erro ao aprovar em lote.';
      toast.error(mensagem);
    } finally {
      setAproving(false);
    }
  }, [diretoriaId, relatoriosParaAprovarLote]);

  if (loading && !diretoriaId) {
    return <Loading />;
  }

  return (
    <Fragment>
      <Container fluid>
        <div className="mb-4">
          <div className="d-flex align-items-end justify-content-between gap-2">
            <h2 className="mb-0">Diretoria Regional</h2>
            <RelatoriosUserLabel className="mb-1" />
          </div>
          <p className="text-muted mb-0">
            Acompanhe os relatórios enviados e utilize <strong>Visualizar</strong> para analisar, aprovar ou devolver ao defensor.
          </p>
          <hr />
        </div>

        {(diretorias.length > 1 || (isAdminSuporte && diretorias.length > 0 && !diretoriaId)) && (
          <div className="mb-3">
            <label htmlFor="diretoria-select" className="form-label">
              Diretoria Regional:
            </label>
            <select
              id="diretoria-select"
              className="form-select"
              value={diretoriaId || ''}
              onChange={(e) => setDiretoriaId(Number(e.target.value))}
            >
              <option value="">Selecione uma diretoria...</option>
              {diretorias.map((diretoria) => (
                <option key={diretoria.id} value={diretoria.id}>
                  {diretoria.nome}
                </option>
              ))}
            </select>
          </div>
        )}

        {!diretoriaId && diretorias.length === 0 && (
          <Alert variant="warning">
            <strong>Atenção!</strong> Você não possui uma diretoria regional associada ao seu usuário.
            {isAdminSuporte
              ? ' Nenhuma diretoria regional foi encontrada no cadastro.'
              : ' Entre em contato com o administrador do sistema para configurar sua lotação na diretoria regional.'}
          </Alert>
        )}

        {!diretoriaId && diretorias.length > 0 && (
          <Alert variant="info">
            Selecione uma diretoria regional para visualizar os relatórios.
          </Alert>
        )}

        {diretoriaId && (
          <>
            <RelatoriosAprovacaoFilter onFilterChange={handleFilterChange} />

            <RelatoriosAprovacaoList
              relatorios={relatorios}
              loading={loading}
              onView={handleView}
              onApproveBatch={handleApproveBatch}
              approvingBatch={aproving}
            />
          </>
        )}

        <Modal
          show={showApproveBatchModal}
          onHide={() => (aproving ? null : setShowApproveBatchModal(false))}
          centered
          backdrop="static"
          keyboard={!aproving}
        >
          <Modal.Header closeButton={!aproving}>
            <Modal.Title>Confirmar aprovação</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-0">
              Deseja aprovar <strong>{relatoriosParaAprovarLote.length}</strong> relatório(s) selecionado(s)?
            </p>
            <p className="text-muted small mt-2 mb-0">
              Após a aprovação, os relatórios seguirão para a Corregedoria.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              disabled={aproving}
              onClick={() => {
                setShowApproveBatchModal(false);
                setRelatoriosParaAprovarLote([]);
              }}
            >
              Cancelar
            </Button>
            <Button variant="success" disabled={aproving} onClick={() => void confirmarAprovacaoLote()}>
              {aproving ? 'Aprovando...' : 'Confirmar aprovação'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Fragment>
  );
}